<script lang="ts" setup>
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import { useRouter } from 'vue-router'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import ProductSpecificationForm from '@/components/features/productSpecification/ProductSpecificationForm.vue'
import { useProductSpecificationForm } from '@/composables/useProductSpecificationForm'
import YarnAndStitchDetails from '@/components/features/productSpecification/YarnAndStitchDetails.vue'
import {computed, ref, onMounted} from "vue";
import {FabricStatusEnum} from "@/types/product-types";
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { Icon } from '@iconify/vue'
import { archiveProductSpecification, unarchiveProductSpecification, getProductById } from '@/api/products'

const router = useRouter()
const { values, handleSubmit, setErrors, showToast, formType } = useProductSpecificationForm('view')

const displayTitle = computed(() => {
  if (!values) return 'Product Specification'

  if (values.fabricStatus === FabricStatusEnum.Finished) {
    return values.finishedReference || 'Product Specification'
  } else if (values.fabricStatus === FabricStatusEnum.Unfinished) {
    return values.unfinishedReference || 'Product Specification'
  }

  return 'Product Specification'
})

const knittingOut = ref<any[]>([])
const yarnSetOut = [
{
    id: '',
    cottonFeeder: 'Feeder 1',
    woolFeeder: 'Feeder 2',
    cottonColor: 'Red',
    woolColor: 'Blue',
    fabricWeight: {
      relaxedWidth: 0,
      weightPerSqMeter: 0,
      courses: 0,
      wales: 0,
    },
    finishedWeight: {
      relaxedWidth: 0,
      weightPerSqMeter: 0,
      courses: 0,
      wales: 0,
    }
  }
];

const productId = router.currentRoute.value.params.id as string
const productData = ref<any>(null)

onMounted(async () => {
  try {
    productData.value = await getProductById(productId)
    if (productData.value?.knittingSetOuts) {
      knittingOut.value = productData.value.knittingSetOuts
    }
  } catch (error) {
    console.error('Error loading product data:', error)
  }
})

const isArchived = computed(() => {
  const fromValues = values?.isArchived === true
  const fromDirectData = productData.value?.isArchived === true
  return fromValues || fromDirectData
})

const handleEditProductSpecification = () => {
  router.push(`/productSpecification/${productId}/edit`)
}

const handleToggleArchive = async () => {
  try {
    if (isArchived.value) {
      await unarchiveProductSpecification(productId)
      showToast('success', 'Product Specification Unarchived!', 'The product specification has been successfully unarchived.')
      window.location.reload()
    } else {
      await archiveProductSpecification(productId)
      showToast('success', 'Product Specification Archived!', 'The product specification has been successfully archived.')
      router.push('/productSpecification/archived')
    }
  } catch (error: any) {
    const action = isArchived.value ? 'Unarchive' : 'Archive'
    showToast('error', `${action} Failed!`, error.response?.data?.message || `Failed to ${action.toLowerCase()} product specification`)
  }
}
</script>
<template>
  <PageHeader :title="displayTitle">
    <template #buttons>
      <div class="flex space-x-4">
        <Button
          outlined
          class="flex items-center gap-x-2"
          @click="handleToggleArchive"
        >
          <span>{{ isArchived ? 'Unarchive Product Specification' : 'Archive Product Specification' }}</span>
          <Icon :icon="isArchived ? 'material-symbols:unarchive' : 'material-symbols:archive'" class="text-xl" />
        </Button>
        <Button class="flex items-center gap-x-2" @click="handleEditProductSpecification" >
          <span> Edit Product Specification </span>
          <Icon icon="basil:edit-solid" class="text-xl" />
        </Button>
      </div>
    </template>
  </PageHeader>

  <TabView class="bg-white rounded-md pb-2">
    <TabPanel header="Product Specification Details">
      <ProductSpecificationForm
        :type="formType"
        :values="values"
        :onSubmit="handleEditProductSpecification"
        :setErrors="setErrors"
      />
    </TabPanel>
    <TabPanel header="Yarn and Stitch Details">
      <YarnAndStitchDetails  :knittingOut="knittingOut" :yarnSetOut="yarnSetOut" />
    </TabPanel>
  </TabView>
</template>
